// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Builds the optional source-record hierarchy shown by Contract Review.
 *
 * Connectors may identify several imported documents as one business record,
 * while manual and existing production workspaces remain flat. This adapter
 * isolates that connector contract from legacy request grouping and keeps the
 * dashboard presentation independent of any specific provider.
 */
import type { DocumentListItemWithPipelines } from '../models'
import type { SourceDisplayField } from '@/subsystems/documents'

export type WorkspaceDocumentEntry =
  | {
    kind: 'document'
    key: string
    document: DocumentListItemWithPipelines
  }
  | {
    kind: 'group'
    key: string
    groupKey: string
    groupName: string
    sourceProvider: string
    sourceDisplayFields: SourceDisplayField[]
    recordTypes: WorkspaceSourceRecordType[]
  }

export interface WorkspaceSourceRecordType {
  key: string
  recordType: string
  documents: DocumentListItemWithPipelines[]
}

function documentMatches(document: DocumentListItemWithPipelines, query: string) {
  return document.documentId.toLowerCase().includes(query)
    || (document.latestFilename ?? document.name ?? '').toLowerCase().includes(query)
    || document.externalId?.toLowerCase().includes(query)
    || document.sourceDisplayFields.some(field => (
      field.label.toLowerCase().includes(query)
      || field.value.toLowerCase().includes(query)
    ))
}

function groupIdentity(sourceProvider: string, groupKey: string) {
  return JSON.stringify([sourceProvider, groupKey])
}

function mergeSourceDisplayFields(
  existing: SourceDisplayField[],
  incoming: SourceDisplayField[],
) {
  const merged = [...existing]
  for (const field of incoming) {
    if (!merged.some(candidate => candidate.key === field.key)) merged.push(field)
  }
  return merged
}

export function buildWorkspaceDocumentEntries(
  documents: DocumentListItemWithPipelines[],
  filter = '',
  groupBySourceRecord = false,
): WorkspaceDocumentEntry[] {
  const entries: WorkspaceDocumentEntry[] = []
  const groups = new Map<string, Extract<WorkspaceDocumentEntry, { kind: 'group' }>>()

  for (const document of documents) {
    const groupKey = document.sourceGroupKey
    const groupName = document.sourceGroupName
    if (groupBySourceRecord && (groupKey === null) !== (groupName === null)) {
      throw new Error(`Document ${document.documentId} has incomplete source grouping`)
    }
    if (!groupBySourceRecord || groupKey === null || groupName === null) {
      entries.push({ kind: 'document', key: document.documentId, document })
      continue
    }
    if (!document.sourceProvider) {
      throw new Error(`Document ${document.documentId} has source grouping without a provider`)
    }
    if (!document.recordType) {
      throw new Error(`Document ${document.documentId} has source grouping without a record type`)
    }

    const identity = groupIdentity(document.sourceProvider, groupKey)
    const existing = groups.get(identity)
    if (existing) {
      // cm-coding: One provider group key identifies one business record, but
      // its document revisions can carry different optional display fields.
      // Merge those fields by key while keeping the provider identity fixed so
      // UI context cannot combine two separately auditable records.
      if (existing.groupName !== groupName) {
        throw new Error(`Source group ${groupKey} has conflicting names`)
      }
      existing.sourceDisplayFields = mergeSourceDisplayFields(
        existing.sourceDisplayFields,
        document.sourceDisplayFields,
      )
      const recordType = existing.recordTypes.find(
        item => item.recordType === document.recordType,
      )
      if (recordType) recordType.documents.push(document)
      else {
        existing.recordTypes.push({
          key: JSON.stringify([identity, document.recordType]),
          recordType: document.recordType,
          documents: [document],
        })
      }
      continue
    }

    const group: Extract<WorkspaceDocumentEntry, { kind: 'group' }> = {
      kind: 'group',
      key: identity,
      groupKey,
      groupName,
      sourceProvider: document.sourceProvider,
      sourceDisplayFields: document.sourceDisplayFields,
      recordTypes: [{
        key: JSON.stringify([identity, document.recordType]),
        recordType: document.recordType,
        documents: [document],
      }],
    }
    groups.set(identity, group)
    entries.push(group)
  }

  const query = filter.trim().toLowerCase()
  if (!query) return entries

  const filtered: WorkspaceDocumentEntry[] = []
  for (const entry of entries) {
    if (entry.kind === 'document') {
      if (documentMatches(entry.document, query)) filtered.push(entry)
      continue
    }
    if (
      entry.groupKey.toLowerCase().includes(query)
      || entry.groupName.toLowerCase().includes(query)
      || entry.sourceProvider.toLowerCase().includes(query)
      || entry.sourceDisplayFields.some(field => (
        field.label.toLowerCase().includes(query)
        || field.value.toLowerCase().includes(query)
      ))
    ) {
      filtered.push(entry)
      continue
    }
    const matchingRecordTypes = entry.recordTypes.flatMap((recordType) => {
      if (recordType.recordType.toLowerCase().includes(query)) return [recordType]
      const matchingDocuments = recordType.documents.filter(
        document => documentMatches(document, query),
      )
      return matchingDocuments.length > 0
        ? [{ ...recordType, documents: matchingDocuments }]
        : []
    })
    if (matchingRecordTypes.length > 0) {
      filtered.push({ ...entry, recordTypes: matchingRecordTypes })
    }
  }
  return filtered
}

export function documentsInEntry(entry: WorkspaceDocumentEntry) {
  return entry.kind === 'document'
    ? [entry.document]
    : entry.recordTypes.flatMap(recordType => recordType.documents)
}

export function documentsInEntries(entries: WorkspaceDocumentEntry[]) {
  return entries.flatMap(documentsInEntry)
}
